import { createTransferInstruction, getAssociatedTokenAddress } from "@com.put/ppl-token"
import {
    Connection as Connection_put,
    PublicKey as PublicKey_put,
    SystemProgram as SystemProgram_put,
    Transaction as Transaction_put
} from "@com.put/web3.js"
import Client from "@walletconnect/sign-client"
import { SessionTypes } from "@walletconnect/types"
import base58 from "bs58"
import {
    BrowserProvider,
    Contract,
    Eip1193Provider,
    formatUnits,
    Interface,
    isAddress,
    JsonRpcProvider,
    parseEther,
    parseUnits,
    toQuantity
} from "ethers"

import ERC20 from "@/abi/ERC20.json"
import Pay from "@/abi/Pay.json"
import { StoreOrder } from "@/store/payment"
import { createTokenAccountTransaction } from "@/utils/put_utils"

import WalletConnectSendTransaction from "./send"
import { getNonce, getTrustWalletInjectedProvider, TronLink, TronWeb, useWeb3Provider } from "./useContract"

class Payment {
    private order: StoreOrder
    private address: string
    private pendingOpen: () => void

    constructor(
        address: string,
        order: StoreOrder,
        pendingOpen: () => void
    ) {
        this.address = address
        this.order = order
        this.pendingOpen = pendingOpen
    }

    async approveToken(provider: BrowserProvider) {
        try {
            const { 
                contract,
                pay_address,
                amount,
                decimal,
                token,
                chain
            } = this.order;
            const signer = await provider.getSigner();
            const address = await signer.getAddress();
            console.log("approve chain ----->", chain, address, contract);
            const tokenContract = new Contract(contract, ERC20, signer);
            const formatAmount = parseUnits(amount, decimal);
            const allowance = await tokenContract
                .allowance(address, pay_address)
                .catch((error) => {
                    console.log("allowance error: ", error);
                    if (error.message.indexOf('value="0x"') > 0) {
                        console.log("allowance is none");
                        return BigInt(0);
                    }
                });

            if (allowance === undefined) {
                throw new Error("Network error, please try again");
            }

            console.log(formatAmount, allowance, formatAmount > allowance, address);

            if (formatAmount > allowance) {
                console.log("start approve------>", parseUnits(amount, decimal));
                const apvTx = await tokenContract.approve(
                    pay_address,
                    parseUnits(amount, decimal)
                );
                await apvTx.wait();
                console.log(`${token} approved!`, apvTx.hash);
                return;
            }

            console.log("allowance is enough");
        } catch (error) {
            const { code, message, reason } = error || {};

            if (code === "UNKNOWN_ERROR") {
                throw new Error("Network error, please try again");
            }

            if (code === 4001 || code === "ACTION_REJECTED") {
                throw new Error("User rejected");
            }

            throw new Error(reason || message);
        }
    }

    async startPayment(provider: BrowserProvider) {
        try {
            const {
                pay_address,
                amount,
                decimal,
                contract,
                chain_info
            } = this.order;
            const formatAmount = parseUnits(amount, decimal);
            const singer = await provider.getSigner();
            const address = await singer.getAddress();
            const network = await provider.getNetwork();
            const nonceRec = await getNonce(chain_info.rpc, address);
            const nonce = nonceRec + 1;
            const txData: Record<string, any> = {
                from: address,
            };
            console.log("\ncurrent address: ", address, "\ncurrent network: ", network, "\ncurrent nonce: ", nonce);

            if (isAddress(contract)) {
                txData.data = new Interface(ERC20).encodeFunctionData("transfer", [
                    pay_address,
                    formatAmount,
                ]);
                txData.to = contract
            } else {
                txData.value = toQuantity(parseEther(amount));
                txData.to = pay_address;
                console.log("transfer coin params: ", contract, txData.value);
            }

            console.log("transaction data: ", txData);
            
            const tx = await singer.sendTransaction({
                ...txData,
                blockTag: "latest",
                nonce,
            });
            const transaction = await provider.waitForTransaction(tx.hash, 1);
            
            console.log("transaction wait for one confirmed success: ", transaction);

            if (!transaction) {
                throw new Error("Transaction not found!");
            }

            await tx.wait();
            console.log(tx.hash);
            return tx.hash;
        } catch (error) {
            console.log(
                "payment error: ",
                error.code,
                "\n",
                error.reason,
                "\n",
                error.message
            );
            const { code, message, reason } = error;

            if (error.code === "UNKNOWN_ERROR") {
                throw new Error("Network error, please try again");
            }

            if (code === "UNKNOWN_ERROR" || !reason) {
                return;
            }

            throw new Error(reason || message);
        }
    }

    async metamask() {
        return new Promise(async (resolve, reject) => {
        try {
            this.pendingOpen();
            let { Web3Provider } = await useWeb3Provider();

            if (!Web3Provider) {
                reject({ message: "Please install metamask extension!" });
                return;
            }

            let tx;
            this.pendingOpen();
            const { contract } = this.order;

            if (isAddress(contract)) {
                await this.approveToken(Web3Provider);
            }

            tx = await this.startPayment(Web3Provider);

            resolve(tx);
        } catch (err) {
            err ? reject(err) : resolve("");
        }
        });
    }

    private async TronLinkSend(tronWeb: TronWeb, to: string, amount: string, token?: string) {
        if (token) {
            let contract = await tronWeb.contract().at(token)
            return contract.transfer(to, amount).send({
                feeLimit: 100_000_000,
                callValue: 0,
                // shouldPollResponse: true
            })
        }

        let tx = await tronWeb.transactionBuilder.sendTrx(to, +amount)
        let signedTx = await tronWeb.trx.sign(tx)
        return tronWeb.trx.sendRawTransaction(signedTx)
    }

    async tronLink(_?: any, tronWeb?: TronWeb) {
        const link = tronWeb || TronWeb();

        if (!link) {
            return new Promise((_, reject) => {
                reject({ message: "Please check your extension wallet!" });
            });
        }

        console.log("start tron transaction----->");
        return new Promise(async (resolve, reject) => {
            new Promise<Exclude<ReturnType<typeof TronLink>, null>>((_resolve) => {
                let web: ReturnType<typeof TronLink> = null, timer: ReturnType<typeof setInterval> | null = null;
                timer = setInterval(() => {
                    web = tronWeb || TronWeb();
                    if (web && timer) {
                        _resolve(web);
                        clearInterval(timer);
                    }
                }, 100);
            }).then(async (tronWeb) => {
                this.pendingOpen();
                let contract = await tronWeb.contract(
                    ERC20 as any,
                    this.order.contract
                );

                this.TronLinkSend(
                    tronWeb,
                    this.order.pay_address,
                    parseUnits(this.order.amount, this.order.decimal).toString(),
                    this.order.contract === "TRX" ? void 0 : this.order.contract
                )
                .then(resolve)
                .catch(reject);
            });
        });
    }

    private async getAllowance() {
        console.log("start get allowance", this.order.contract, this.order.chain, this.order.chain_info.rpc);
        const contract = new Contract(
            this.order.contract,
            ERC20,
            new JsonRpcProvider(this.order.chain_info.rpc)
        );
        let num = await contract.allowance(
            this.address,
            this.order.pay_address
        );
        let decimals: number = await contract.decimals();
        let allowance = +formatUnits(num, decimals);
        console.log(allowance, "allowance");
        return allowance > +this.order.amount;
    }

    private async loopGetAllowance() {
        let then = await this.getAllowance()
        if (!then) {
            await new Promise(resolve => {
                setTimeout(() => {
                    resolve(this.loopGetAllowance())
                }, 1_000)
            })
        }
    }

    async broearnWallet(session: SessionTypes.Struct) {
        let chain: string = "put:"
        // if (this.isMobile && !isBroearn) {
        //     let url = `https://www.broearn.com/dapp/${window.location.href}`

        //     this.open(url)
        //     return Promise.reject()
        // }
        return new Promise(async (resolve, reject) => {
            const send = async ({
                provider, 
                sendTransaction
            } : {
                provider?: BrowserProvider,
                sendTransaction?: WalletConnectSendTransaction
            }) => {
                this.pendingOpen()

                switch (this.order.chain) {
                    case "PUT":
                        chain = "put:"
                        const connection = new Connection_put(this.order.chain_info.rpc)
                        const pubKey = new PublicKey_put(this.address)
                        const transaction = new Transaction_put()
                        const targetPubKey = new PublicKey_put(this.order.pay_address)
                        transaction.feePayer = transaction.feePayer ?? pubKey
                        const recentBlockhash = (await connection.getLatestBlockhash("max")).blockhash
                        transaction.recentBlockhash = recentBlockhash

                        if (this.order.contract === "PUT") {
                            transaction.add(SystemProgram_put.transfer({
                                fromPubkey: pubKey,
                                lamports:  parseUnits(this.order.amount, this.order.decimal),
                                toPubkey: new PublicKey_put(this.order.pay_address)
                            }))
                        } else {
                            const mintPublicKey = new PublicKey_put(this.order.contract)
                            let targetTokenAccount: PublicKey_put
                            let baseTokenAccount = await connection.getTokenAccountsByOwner(
                                pubKey,
                                {
                                    mint: mintPublicKey
                                }
                            )

                            const associatedToken = await getAssociatedTokenAddress(
                                mintPublicKey,
                                targetPubKey
                            )

                            if (await connection.getAccountInfo(associatedToken, "confirmed")) {
                                targetTokenAccount = associatedToken
                            }
                            else {
                                const { transaction: createAccountTransaction, newAccountPubkey } = await createTokenAccountTransaction({
                                    payer: pubKey,
                                    owner: targetPubKey,
                                    mintPublicKey
                                })
                                transaction.add(createAccountTransaction)
                                targetTokenAccount = newAccountPubkey
                            }

                            transaction.add(createTransferInstruction(
                                baseTokenAccount.value[0].pubkey,
                                targetTokenAccount,
                                pubKey,
                                parseUnits(this.order.amount, this.order.decimal)
                            ))
                        }

                        if (!!window.bw) {
                            await window.bw.put.signAndSendTransaction({
                                transaction: base58.encode(transaction.serialize({ verifySignatures: false })),
                                session: localStorage.getItem("bw_session")!
                            }).then(resolve).catch(err => {
                                reject(err.errorMessage)
                            })
                        }
                        else {
                            await sendTransaction?.put(
                                { transaction: base58.encode(transaction.serialize({ verifySignatures: false })) },
                                chain
                            ).then(resolve).catch(err => {
                                reject(err.message)
                            })
                        }
                        break
                    case "Tron":
                        const tronLink: any | null  = window.bw?.tron || null
                        
                        if (tronLink?.tronWeb?.ready) {
                            console.log("broearn tron web: ", tronLink.tronWeb)
                            this.tronLink(undefined, tronLink.tronWeb).then(resolve).catch(reject)
                        }
                        break
                    default:
                        try {
                            chain = "eip155:" + this.order.chain_id

                            if (isAddress(this.order.contract)) {
                                const allowance = await this.getAllowance()
                                console.log("broearn wallet allowance: ", allowance)
                                if (!allowance) {
                                    const contract = new Contract(
                                        this.order.contract,
                                        ERC20,
                                        new JsonRpcProvider(this.order.chain_info.rpc)
                                    )
                                    const totalSupply: BigInt = await contract.totalSupply()
                                    
                                    if (provider) {
                                        await this.approveToken(provider)
                                        await this.startPayment(provider).then(resolve).catch(reject);
                                    } else if (sendTransaction) {
                                        await sendTransaction.eth(
                                            [
                                                {
                                                    from: this.address,
                                                    to: this.order.contract,
                                                    data: new Interface(ERC20).encodeFunctionData(
                                                        "approve",
                                                        [
                                                            this.order.pay_address,
                                                            totalSupply
                                                        ]
                                                    )
                                                }
                                            ],
                                            chain
                                        )
                                        await this.loopGetAllowance()
                                        await sendTransaction?.eth(
                                            [
                                                {
                                                    from: this.address,
                                                    to: this.order.contract,
                                                    data: new Interface(ERC20).encodeFunctionData(
                                                        "transfer",
                                                        [
                                                            this.order.pay_address,
                                                            parseUnits(this.order.amount, this.order.decimal)
                                                        ]
                                                    )
                                                }
                                            ],
                                            chain
                                        ).then(resolve).catch(err => {
                                            reject(err.message)
                                        })
                                    }
                                }
                            } else if (sendTransaction) {
                               await sendTransaction.eth(
                                    [
                                        {
                                            from: this.address,
                                            to: this.order.pay_address,
                                            data: new Interface(Pay).encodeFunctionData(
                                                "transfer(string)",
                                                [this.order.trade_no]
                                            ),
                                            value: toQuantity(parseUnits(this.order.amount, this.order.decimal))
                                        }
                                    ],
                                    chain
                                ).then(resolve).catch(err => {
                                    reject(err.message)
                                }) 
                            }
                        } catch (err) {
                            reject(typeof err === "string" ? err : err.message)
                        }
                        break;
                }
            }

            if (!!window.bw) {
                const chianType = this.order.chain.toLocaleLowerCase()
                let provider: any = new BrowserProvider(window.bw["eth"])

                if (["put", "tron", "solana"].includes(chianType)) {
                    provider = window.bw[chianType]
                    console.log(window.bw[chianType])
                }  else {
                    console.log(window.bw.eth)
                }

                
                send({ provider: provider as any })
            }

            else {
                const client = await Client.init({
                    relayUrl: "wss://relay.walletconnect.org",
                    projectId: "d79c84a4ed043335456a5bdf06ad7a53",
                    metadata: {
                        name: "Reelpay",
                        description: "Provides safe and convenient cryptocurrency payment solutions with the goal of financializing cryptocurrency.",
                        icons: ["https://reel-block-n.oss-ap-southeast-1.aliyuncs.com/base/202303/8ed0bf7c52c97a76.png"],
                        url: window.location.host
                    }
                })

                const sendTransaction = new WalletConnectSendTransaction({ client, session })

                send({sendTransaction})
            }
        })
    }

    async walletconnect(session: SessionTypes.Struct) {
        let chain: string
        switch (this.order.chain) {
            case "PUT":
                chain = "put:"
                break
            case "Tron":
                chain = "tron:"
                break
            default:
                chain = "eip155:" + this.order.chain_id
        }

        return new Promise(async (resolve, reject) => {
            const send = async (sendTransaction?: WalletConnectSendTransaction) => {
                this.pendingOpen()
                switch (this.order.chain) {
                    case "PUT":
                        chain = "put:"
                        const connection = new Connection_put(this.order.chain_info.rpc)
                        const pubKey = new PublicKey_put(this.address)
                        const recentBlockhash = (await connection.getLatestBlockhash("max")).blockhash
                        const transaction = new Transaction_put()
                        transaction.feePayer = transaction.feePayer ?? pubKey
                        transaction.recentBlockhash = recentBlockhash
                        const ABI = JSON.parse(this.order.chain_info.abi)
                        const targetPubKey = new PublicKey_put(this.order.pay_address)
                        if (this.order.contract === "PUT") {
                            transaction.add(SystemProgram_put.transfer({
                                fromPubkey: pubKey,
                                lamports: parseUnits(this.order.amount, this.order.decimal),
                                toPubkey: new PublicKey_put(targetPubKey)
                            }))
                        }
                        else {
                            const mintPublicKey = new PublicKey_put(this.order.contract)
                            let targetTokenAccount: PublicKey_put
                            let baseTokenAccount = await connection.getTokenAccountsByOwner(
                                pubKey,
                                {
                                    mint: mintPublicKey
                                }
                            )

                            const associatedToken = await getAssociatedTokenAddress(
                                mintPublicKey,
                                targetPubKey
                            )

                            if (await connection.getAccountInfo(associatedToken, "confirmed")) {
                                targetTokenAccount = associatedToken
                            }
                            else {
                                const { transaction: createAccountTransaction, newAccountPubkey } = await createTokenAccountTransaction({
                                    payer: pubKey,
                                    owner: targetPubKey,
                                    mintPublicKey
                                })
                                transaction.add(createAccountTransaction)
                                targetTokenAccount = newAccountPubkey
                            }

                            transaction.add(createTransferInstruction(
                                baseTokenAccount.value[0].pubkey,
                                targetTokenAccount,
                                pubKey,
                                parseUnits(this.order.amount, this.order.decimal)
                            ))
                        }
                        await sendTransaction?.put(
                            { transaction: base58.encode(transaction.serialize({ verifySignatures: false })) },
                            chain
                        ).then(resolve).catch(err => {
                            reject(err.message)
                        })
                        break
                    case "Tron":
                        break
                    default:
                        if (isAddress(this.order.contract)) {
                            try {
                                if (!await this.getAllowance()) {
                                    const contract = new Contract(
                                        this.order.contract,
                                        ERC20,
                                        new JsonRpcProvider(this.order.chain_info.rpc)
                                    )
                                    const totalSupply: BigInt = await contract.totalSupply()
                                    await sendTransaction?.eth(
                                        [{
                                            from: this.address,
                                            to: this.order.contract,
                                            data: new Interface(ERC20).encodeFunctionData(
                                                "approve",
                                                [
                                                    this.order.pay_address,
                                                    totalSupply
                                                ]
                                            )
                                        }],
                                        chain
                                    )
                                    await this.loopGetAllowance()
                                    await sendTransaction?.eth(
                                        [
                                            {
                                                from: this.address,
                                                to: this.order.contract,
                                                data: new Interface(ERC20).encodeFunctionData(
                                                    "transfer",
                                                    [
                                                        this.order.pay_address,
                                                        parseUnits(this.order.amount, this.order.decimal)
                                                    ]
                                                )
                                            }
                                        ],
                                        chain
                                    ).then(resolve).catch(err => {
                                        reject(err.message)
                                    })
                                }
                            } catch (err) {
                                reject(err.message)
                                return
                            }
                        } else {
                            await sendTransaction?.eth(
                                [
                                    {
                                        from: this.address,
                                        to: this.order.pay_address,
                                        data: new Interface(Pay).encodeFunctionData(
                                            "transfer(string)",
                                            [this.order.trade_no]
                                        ),
                                        value: toQuantity(parseUnits(this.order.amount, this.order.decimal))
                                    }
                                ],
                                chain
                            ).then(resolve).catch(err => {
                                reject(err.message)
                            })
                        }
                }
            }

            const client = await Client.init({
                relayUrl: "wss://relay.walletconnect.org",
                projectId: "d79c84a4ed043335456a5bdf06ad7a53",
                metadata: {
                    name: "Reelpay",
                    description: "Provides safe and convenient cryptocurrency payment solutions with the goal of financializing cryptocurrency.",
                    icons: ["https://reel-block-n.oss-ap-southeast-1.aliyuncs.com/base/202303/8ed0bf7c52c97a76.png"],
                    url: window.location.host
                }
            })

            const sendTransaction = new WalletConnectSendTransaction({ client, session })
            send(sendTransaction)
        })
    }
    
    async okx() {
        return new Promise(async (resolve, reject) => {
            try {
                if (this.order.chain === "Tron") {
                    console.log("okx start tron nile transaction")
                    const tronLink: any | null  = window.okxwallet?.tronLink || null
                    console.log("okx tron link: ", tronLink)
                    if (tronLink?.ready) {
                        console.log("okx tron web: ", window.okxwallet.tronWeb)
                        this.tronLink(undefined, window.okxwallet.tronWeb).then(resolve)
                    }
                } else {
                    let tx;
                    this.pendingOpen()
                    const { contract } = this.order
                    const provider = new BrowserProvider(window.okxwallet)
    
                    if (isAddress(contract)) {
                        await this.approveToken(provider)
                    }
    
                    tx = await this.startPayment(provider)    

                    resolve(tx)
                }
            } catch (err) {
                err ? reject(err) : resolve("")
            }
        })
    }

    async trust() {
        return new Promise(async (resolve, reject) => {
            try {
                let tx;
                this.pendingOpen()
                const { contract } = this.order;
                const trustProvider = await getTrustWalletInjectedProvider();
                if (!trustProvider) {
                    throw new Error("Trust wallet is not installed!")
                }

                const provider = new BrowserProvider(trustProvider as Eip1193Provider);

                if (isAddress(contract)) {
                    await this.approveToken(provider)
                }

                tx = await this.startPayment(provider)

                resolve(tx)
            } catch (err) {
                err ? reject(err) : resolve("")            
            }
        })
    }
}

export { Payment }
export default Payment

